/**
 * BranchFormTabs Component
 *
 * Handles the tabbed interface for Branches and Contacts management.
 * Separated from BranchManager for better separation of concerns.
 */

import StickyFooter from '@/components/common/StickyFooter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useFormsTranslation } from '@/hooks/useTranslation';
import type { Branch } from '@/types/branch.types';
import type { BranchContact } from '@/types/branchContact.types';
import { Building, CheckCircle2, Plus, User, XCircle } from 'lucide-react';
import React, { useState } from 'react';

interface BranchFormTabsProps {
  branches: Branch[];
  contacts: BranchContact[];
  onAddBranch: () => void;
  onAddContact: () => void;
  children: {
    branches: React.ReactNode;
    contacts: React.ReactNode;
  };
  className?: string;
  // Validation states for each tab
  branchesValidationState?: 'valid' | 'invalid' | 'pending';
  contactsValidationState?: 'valid' | 'invalid' | 'pending';
  // Validation callbacks
  onValidateBranches?: () => Promise<boolean> | boolean;
  onValidateContacts?: () => Promise<boolean> | boolean;
  // Submit functionality (Sub-item 1.3)
  onSubmit?: () => Promise<void> | void;
  isSubmitting?: boolean;
}

const BranchFormTabs: React.FC<BranchFormTabsProps> = ({
  branches,
  contacts,
  onAddBranch,
  onAddContact,
  children,
  className,
  branchesValidationState = 'pending',
  contactsValidationState = 'pending',
  onValidateBranches,
  onValidateContacts,
  onSubmit,
  isSubmitting = false,
}) => {
  const [selectedTab, setSelectedTab] = useState('branches');
  const [isValidating, setIsValidating] = useState(false);
  const { toast } = useToast();
  const { t } = useFormsTranslation();

  // Tab order for navigation
  const tabs = ['branches', 'contacts'];
  const currentTabIndex = tabs.indexOf(selectedTab);
  const isLastTab = currentTabIndex === tabs.length - 1;
  const isFirstTab = currentTabIndex === 0;

  // Handle tab navigation with validation
  const handleTabNavigation = async (targetTab: string) => {
    if (targetTab === selectedTab) return;

    setIsValidating(true);

    try {
      // Validate current tab before switching
      if (selectedTab === 'branches' && onValidateBranches) {
        await onValidateBranches();
      } else if (selectedTab === 'contacts' && onValidateContacts) {
        await onValidateContacts();
      }

      // Always allow navigation but validation state will show in tab header
      setSelectedTab(targetTab);
    } catch (error) {
      console.error('Validation error:', error);
      // Still allow navigation but with error state
      setSelectedTab(targetTab);
    } finally {
      setIsValidating(false);
    }
  };

  // Handle next button click
  const handleNextTab = async () => {
    if (isLastTab) return;

    const nextTabIndex = currentTabIndex + 1;
    const nextTab = tabs[nextTabIndex];
    await handleTabNavigation(nextTab);
  };

  // Handle previous button click (Sub-item 1.2)
  const handlePreviousTab = async () => {
    if (isFirstTab) return;

    const previousTabIndex = currentTabIndex - 1;
    const previousTab = tabs[previousTabIndex];
    await handleTabNavigation(previousTab);
  };

  // Handle submit button click (Sub-item 1.3)
  const handleSubmit = async () => {
    if (!onSubmit) return;

    setIsValidating(true);

    try {
      // Validate all tabs before submitting
      let allTabsValid = true;
      let validationErrors: string[] = [];

      if (onValidateBranches) {
        const branchesValid = await onValidateBranches();
        if (!branchesValid) {
          allTabsValid = false;
          validationErrors.push(t('branch.validation.tabNames.branches'));
        }
      }

      if (onValidateContacts) {
        const contactsValid = await onValidateContacts();
        if (!contactsValid) {
          allTabsValid = false;
          validationErrors.push(t('branch.validation.tabNames.contacts'));
        }
      }

      // Always proceed with submit but show appropriate message
      if (allTabsValid) {
        await onSubmit();
        toast({
          title: t('branch.messages.titles.success'),
          description: t('branch.messages.submitSuccess'),
          variant: 'default',
        });
      } else {
        toast({
          title: t('branch.messages.titles.validationError'),
          description: t('branch.messages.validationError', {
            tabs: validationErrors.join(', '),
          }),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: t('branch.messages.titles.error'),
        description: t('branch.messages.submitError'),
        variant: 'destructive',
      });
    } finally {
      setIsValidating(false);
    }
  };

  // Get validation icon for a tab
  const getValidationIcon = (
    validationState: 'valid' | 'invalid' | 'pending'
  ) => {
    switch (validationState) {
      case 'valid':
        return <CheckCircle2 className='h-4 w-4 text-green-600' />;
      case 'invalid':
        return <XCircle className='h-4 w-4 text-red-600' />;
      default:
        return null;
    }
  };

  return (
    <div className={`w-full ${className || ''}`}>
      <Tabs
        value={selectedTab}
        onValueChange={handleTabNavigation}
        className='w-full'
      >
        <div className='flex items-center justify-between mb-4'>
          <TabsList>
            <TabsTrigger value='branches' className='flex items-center gap-2'>
              <Building className='h-4 w-4' />
              {t('branch.tabs.branches')}
              <Badge variant='secondary'>{branches.length}</Badge>
              {getValidationIcon(branchesValidationState)}
            </TabsTrigger>
            <TabsTrigger value='contacts' className='flex items-center gap-2'>
              <User className='h-4 w-4' />
              {t('branch.tabs.contacts')}
              <Badge variant='secondary'>{contacts.length}</Badge>
              {getValidationIcon(contactsValidationState)}
            </TabsTrigger>
          </TabsList>

          <div className='flex items-center gap-2'>
            {/* Search functionality can be added here in the future */}
            {/* <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div> */}

            {selectedTab === 'branches' ? (
              <Button onClick={onAddBranch} className='flex items-center gap-2'>
                <Plus className='h-4 w-4' />
                {t('branch.actions.addBranch')}
              </Button>
            ) : (
              <Button
                onClick={onAddContact}
                className='flex items-center gap-2'
                disabled={branches.length === 0}
                title={
                  branches.length === 0
                    ? t('branch.actions.addContactDisabledTooltip')
                    : undefined
                }
              >
                <Plus className='h-4 w-4' />
                {t('branch.actions.addContact')}
              </Button>
            )}
          </div>
        </div>

        {/* Branches Tab */}
        <TabsContent value='branches' className='space-y-4 pb-24'>
          {children.branches}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value='contacts' className='space-y-4 pb-24'>
          {children.contacts}
        </TabsContent>
      </Tabs>

      <StickyFooter
        onPrevious={!isFirstTab ? handlePreviousTab : undefined}
        onNext={!isLastTab ? handleNextTab : undefined}
        onSubmit={onSubmit ? handleSubmit : undefined}
        showPrevious={!isFirstTab}
        showNext={!isLastTab}
        showSubmit={!!onSubmit}
        isValidating={isValidating}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default BranchFormTabs;
